// Typed client for the FastAPI backend. All requests are same-origin and use
// the httpOnly session cookie, so there is no token handling here.

import { createSseParser } from "./sse";

export interface User {
  id: number;
  email: string;
  name: string;
  email_verified: boolean;
}

export interface Notice {
  message: string;
}

export interface Course {
  slug: string;
  title: string;
  description: string;
}

export interface CloneInfo {
  exists: boolean;
  edit_count: number;
  created_at: string | null;
  updated_at: string | null;
  can_undo: boolean;
  original_newer: boolean;
}

export interface CourseDetail extends Course {
  original_updated_at: string;
  clone: CloneInfo;
}

export type Version = "original" | "mine";
export type ChatMode = "ask" | "edit";

export interface CourseContent {
  version: Version;
  html: string;
  updated_at: string;
}

export interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: number;
  role: "user" | "assistant";
  content: string;
  mode: ChatMode;
  status: "ok" | "error";
  created_at: string;
  faq_entry_id: number | null;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
}

export interface FaqEntry {
  id: number;
  question: string;
  answer: string;
  source_message_id: number | null;
  created_at: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

// Set by the auth provider so any 401 drops the user back to the login page.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

async function errorFrom(res: Response): Promise<ApiError> {
  let message = res.statusText || `Request failed (${res.status})`;
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") message = body.detail;
    else if (Array.isArray(body?.detail) && body.detail[0]?.msg) message = body.detail[0].msg;
  } catch {
    /* not JSON */
  }
  return new ApiError(res.status, message);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401 && !path.startsWith("/api/auth/login")) onUnauthorized?.();
  if (!res.ok) throw await errorFrom(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const enc = encodeURIComponent;

export const api = {
  login: (email: string, password: string) => request<User>("POST", "/api/auth/login", { email, password }),
  logout: () => request<void>("POST", "/api/auth/logout"),
  me: () => request<User>("GET", "/api/auth/me"),
  signup: (name: string, email: string, password: string) =>
    request<User>("POST", "/api/auth/signup", { name, email, password }),
  resendVerification: () => request<Notice>("POST", "/api/auth/verify/resend"),
  verifyEmail: (token: string) => request<User>("POST", "/api/auth/verify", { token }),
  forgotPassword: (email: string) => request<Notice>("POST", "/api/auth/password/forgot", { email }),
  checkResetToken: (token: string) => request<Notice>("POST", "/api/auth/password/check", { token }),
  resetPassword: (token: string, password: string) =>
    request<User>("POST", "/api/auth/password/reset", { token, password }),

  courses: () => request<Course[]>("GET", "/api/courses"),
  course: (slug: string) => request<CourseDetail>("GET", `/api/courses/${enc(slug)}`),
  content: (slug: string, version: Version) =>
    request<CourseContent>("GET", `/api/courses/${enc(slug)}/content?version=${version}`),
  createClone: (slug: string) => request<CloneInfo>("POST", `/api/courses/${enc(slug)}/clone`),
  resetClone: (slug: string) => request<CloneInfo>("DELETE", `/api/courses/${enc(slug)}/clone`),
  undoClone: (slug: string) => request<CloneInfo>("POST", `/api/courses/${enc(slug)}/clone/undo`),

  conversations: (slug: string) => request<Conversation[]>("GET", `/api/courses/${enc(slug)}/conversations`),
  createConversation: (slug: string) => request<Conversation>("POST", `/api/courses/${enc(slug)}/conversations`),
  conversation: (id: number) => request<ConversationDetail>("GET", `/api/conversations/${id}`),
  renameConversation: (id: number, title: string) =>
    request<Conversation>("PATCH", `/api/conversations/${id}`, { title }),
  deleteConversation: (id: number) => request<void>("DELETE", `/api/conversations/${id}`),

  faq: (slug: string) => request<FaqEntry[]>("GET", `/api/courses/${enc(slug)}/faq`),
  saveToFaq: (slug: string, messageId: number) =>
    request<FaqEntry>("POST", `/api/courses/${enc(slug)}/faq`, { message_id: messageId }),
  updateFaq: (id: number, patch: { question?: string; answer?: string }) =>
    request<FaqEntry>("PATCH", `/api/faq/${id}`, patch),
  deleteFaq: (id: number) => request<void>("DELETE", `/api/faq/${id}`),
};

// ---------------------------------------------------------------- streaming

export interface StreamHandlers {
  onStart?: (userMessage: Message) => void;
  onDelta?: (text: string) => void;
  onTool?: (text: string) => void;
  onDone?: (result: { message: Message; clone_changed: boolean; clone: CloneInfo | null }) => void;
}

/** Send a chat message and consume the Server-Sent Events response. */
export async function sendMessage(
  conversationId: number,
  body: { content: string; mode: ChatMode; context: Version },
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/conversations/${conversationId}/messages`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (res.status === 401) onUnauthorized?.();
  if (!res.ok || !res.body) throw await errorFrom(res);

  let gotDone = false;
  const parser = createSseParser((event, data) => {
    const payload = data ? JSON.parse(data) : {};
    switch (event) {
      case "start":
        handlers.onStart?.(payload.user_message);
        break;
      case "delta":
        handlers.onDelta?.(payload.text);
        break;
      case "tool":
        handlers.onTool?.(payload.text);
        break;
      case "done":
        gotDone = true;
        handlers.onDone?.(payload);
        break;
    }
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    parser.feed(decoder.decode(value, { stream: true }));
  }
  parser.feed(decoder.decode());
  parser.flush();
  if (!gotDone) throw new ApiError(0, "The connection closed before the answer finished.");
}
